import React from "react";
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Box,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

// Fade + scale animation for dialog content
const fadeScale = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Fade-in animation for image grid container
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Styled DialogContent with animation and scroll if needed
const AnimatedDialogContent = styled(DialogContent)(({ theme }) => ({
    animation: `${fadeScale} 0.3s ease-out`,
    maxHeight: "70vh",
    overflowY: "auto",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),

    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        padding: theme.spacing(2),
    },
}));

// Styled Box for info content
const InfoBox = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: "auto",
	textAlign:"center",
    "& > *:not(:last-child)": {
        marginBottom: theme.spacing(2),
    },
}));

// Image grid container
const ImageGrid = styled("div")(({ theme }) => ({
    flex: 1,
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    padding: theme.spacing(1),
    maxHeight: 320,
    animation: `${fadeIn} 0.4s ease-out`,

    [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(1, 1fr)",
    },
}));

// Each image wrapper: center image inside, zoom on hover
const ImageWrapper = styled("div")(({ theme }) => ({
    borderRadius: '12px',
    overflow: "hidden",
    cursor: "pointer",
    display: "flex",                      // enable flexbox
    alignItems: "center",                 // center vertically
    justifyContent: "center",             // center horizontally
    backgroundColor: "#f5f5f5",           // optional light background
    height: 150,   
	cursor:"none",
	padding:"10px",                       // ensure fixed height
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "& img": {
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",             // prevent stretching
        display: "block",
    },
}));

const BootstrapDialogTitle = (props) => {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle
            sx={{ m: 0, p: 2, fontWeight: "bold" }}
            className="bg-primary text-white text-center"
            {...other}
        >
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    className="btn-close position-absolute"
                    style={{
                        right: 8,
                        top: 8,
                        color: "red"
                    }}
                >
                    <CloseIcon style={{ color: "red" }} />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
};

const CampaignDetail = (props) => {
    const { open, setOpenDetails, campaignId, campaignIdData } = props;
    const { image = [], collections = [], title, description, amount, url } = campaignIdData || {};

    const handleClose = () => {
        setOpenDetails(false);
    };

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: "hidden",
                    maxHeight: "92vh",
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    background: "#ffffff",
                },
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: "blur(6px)",
                    backgroundColor: "rgba(2, 6, 23, 0.6)",
                },
            }}
        >
            <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
                {title}
            </BootstrapDialogTitle>

            {image.length > 0 && (
                // <ImageGrid>
                    image.map((src, idx) => (
                        <ImageWrapper key={idx}>
                            <img
                                src={src}
                                alt={title}
                                loading="lazy"
								className="rounded"
                            />
                        </ImageWrapper>
                    ))
                // </ImageGrid>
            )}

            <AnimatedDialogContent dividers>
                <InfoBox>
                    <Typography variant="body1">
                        <strong>Campaign Id :</strong> {campaignId}
                    </Typography>
                    <Typography variant="body1">
                        <strong>Details :</strong> {description}
                    </Typography>
                    <Typography variant="body1">
                        <strong>Amount :</strong> {amount}
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                        <strong>URL :</strong>{" "}
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
                            {url}
                        </a>
                    </Typography>

                    {collections && collections.length > 0 && (
                        <TableContainer component={Paper} sx={{ mt: 3 }}>
                            <Table aria-label="collections table" size="small">
                                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                                    <TableRow>
                                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                            Collection Name
                                        </TableCell>
                                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                            Collection Detail
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {collections.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{row.collection_name}</TableCell>
                                            <TableCell>{row.details}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </InfoBox>
            </AnimatedDialogContent>
        </Dialog>
    );
};

export default CampaignDetail;
